'use client'

import { useCallback, useRef, useState } from 'react'

export type ConnectShareTokenSessionState = {
  status: 'idle' | 'loading' | 'ready' | 'error'
  dogCardUrl: string | null
  expiresAt: string | null
  errorMessage: string | null
}

export type ConnectShareTokenCreateResult =
  | { ok: true; dogCardUrl: string; expiresAt?: string | null }
  | { ok: false; message: string }

export type UseConnectShareTokenSessionOptions = {
  createShareToken: () => Promise<ConnectShareTokenCreateResult>
  userErrorMessage?: string
}

const DEFAULT_USER_ERROR =
  "We couldn't prepare your dog's Dog Card. Please try again."

/**
 * Keeps one transient share URL per sheet session.
 * Serializes token creation — no duplicate in-flight requests or rapid-tap storms.
 */
export function useConnectShareTokenSession({
  createShareToken,
  userErrorMessage = DEFAULT_USER_ERROR,
}: UseConnectShareTokenSessionOptions) {
  const [state, setState] = useState<ConnectShareTokenSessionState>({
    status: 'idle',
    dogCardUrl: null,
    expiresAt: null,
    errorMessage: null,
  })
  const inFlightRef = useRef<Promise<ConnectShareTokenCreateResult> | null>(null)
  const cachedUrlRef = useRef<string | null>(null)
  const cachedExpiryRef = useRef<string | null>(null)

  const reset = useCallback(() => {
    inFlightRef.current = null
    cachedUrlRef.current = null
    cachedExpiryRef.current = null
    setState({
      status: 'idle',
      dogCardUrl: null,
      expiresAt: null,
      errorMessage: null,
    })
  }, [])

  const ensureToken = useCallback(async (): Promise<
    | { ok: true; dogCardUrl: string; expiresAt: string | null }
    | { ok: false; message: string }
  > => {
    if (cachedUrlRef.current) {
      return {
        ok: true,
        dogCardUrl: cachedUrlRef.current,
        expiresAt: cachedExpiryRef.current,
      }
    }

    if (inFlightRef.current) {
      const result = await inFlightRef.current
      if (result.ok) {
        return {
          ok: true,
          dogCardUrl: result.dogCardUrl,
          expiresAt: result.expiresAt ?? null,
        }
      }
      return { ok: false, message: result.message }
    }

    setState((prev) => ({
      ...prev,
      status: 'loading',
      errorMessage: null,
    }))

    const request = createShareToken()
    inFlightRef.current = request

    try {
      const result = await request
      if (!result.ok) {
        setState({
          status: 'error',
          dogCardUrl: null,
          expiresAt: null,
          errorMessage: result.message,
        })
        return { ok: false, message: result.message }
      }

      cachedUrlRef.current = result.dogCardUrl
      cachedExpiryRef.current = result.expiresAt ?? null
      setState({
        status: 'ready',
        dogCardUrl: result.dogCardUrl,
        expiresAt: result.expiresAt ?? null,
        errorMessage: null,
      })
      return {
        ok: true,
        dogCardUrl: result.dogCardUrl,
        expiresAt: result.expiresAt ?? null,
      }
    } catch {
      const message = userErrorMessage
      setState({
        status: 'error',
        dogCardUrl: null,
        expiresAt: null,
        errorMessage: message,
      })
      return { ok: false, message }
    } finally {
      inFlightRef.current = null
    }
  }, [createShareToken, userErrorMessage])

  return { state, ensureToken, reset }
}
